import { map } from "lodash";
import { Check, ListFilter } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { SKUCommand } from "@/components/sku-command";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetCustomVerificationTypesQuery } from "@/data/queries";
import { TagVerificationTypeLabels, TagVerificationTypes } from "@/data/types";
import { getFilterCount } from "@/lib/pagination";
import { cn } from "@/lib/utils";

import { ResponsiveButton } from "../responsive-button";

export const FilterDropdownMenu = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  const { data: customTypes } = useGetCustomVerificationTypesQuery();

  const setParam = useCallback(
    ({
      key,
      value,
      keysToReset,
    }: {
      key: string;
      value: string;
      keysToReset: string[];
    }) => {
      const tempParams = new URLSearchParams(searchParams);

      for (const key of keysToReset) {
        tempParams.delete(key);
      }

      tempParams.set(key, value);

      setSearchParams(tempParams);
      setOpen(false);
    },
    [searchParams, setSearchParams, setOpen],
  );

  const handleSelectVerifyType = useCallback(
    (verifyType: string) => {
      setParam({
        key: "verifyType",
        value: verifyType,
        keysToReset: ["page", "customVerificationTypeId"],
      });
    },
    [setParam],
  );

  const handleSelectCustomVerifyType = useCallback(
    (id: string) => {
      setParam({
        key: "customVerificationTypeId",
        value: id,
        keysToReset: ["page", "verifyType"],
      });
    },
    [setParam],
  );

  const handleSelectSKU = useCallback(
    (skuId: string) => {
      setParam({
        key: "templateId",
        value: skuId,
        keysToReset: ["page"],
      });
    },
    [setParam],
  );

  const filterCount = useMemo(
    () =>
      getFilterCount({
        searchParams,
        allowedParams: ["verifyType", "customVerificationTypeId", "templateId"],
      }),
    [searchParams],
  );

  const customVerificationTypeId = useMemo(
    () => searchParams.get("customVerificationTypeId"),
    [searchParams],
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <ResponsiveButton
          leftIcon={<ListFilter />}
          rightIcon={filterCount > 0 && <Badge>{filterCount}</Badge>}
          variant="outline"
        >
          Filters
        </ResponsiveButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[175px]">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>SKUs</DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <SKUCommand
                onSelect={handleSelectSKU}
                selectedSKUId={searchParams.get("templateId") || ""}
              />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Verification Type</DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {map(TagVerificationTypes, (type) => (
                <DropdownMenuItem
                  key={type}
                  textValue={type}
                  onSelect={() => handleSelectVerifyType(type)}
                >
                  {type === "custom" ? (
                    <span>All Custom Types</span>
                  ) : (
                    <span>{TagVerificationTypeLabels[type]}</span>
                  )}
                  <Check
                    className={cn(
                      "ml-auto",
                      searchParams.get("verifyType") === type
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </DropdownMenuItem>
              ))}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Custom</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {map(customTypes, (type) => (
                    <DropdownMenuItem
                      key={type.id}
                      textValue={type.name}
                      onSelect={() =>
                        handleSelectCustomVerifyType(type.id.toString())
                      }
                    >
                      {type.name}
                      <Check
                        className={cn(
                          "ml-auto",
                          customVerificationTypeId === type.id.toString()
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
